<template>
    <div class="flex flex-col">
        <!-- Content -->
        <div class="p-6">
            <form @submit.prevent="uploadGuide">
                <!-- Guide Name -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Document Name <span class="text-red-500">*</span>
                    </label>
                    <input v-model="form.name" type="text" required
                        placeholder="Enter a descriptive name for this document"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        :class="{ 'border-red-500': errors.name }" />
                    <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
                </div>

                <!-- Description -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea v-model="form.description" rows="3" placeholder="Optional description of the document"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"></textarea>
                </div>

                <!-- File Upload -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        File <span class="text-red-500">*</span>
                    </label>

                    <!-- Drag and Drop Area -->
                    <div @drop="handleDrop" @dragover.prevent @dragenter.prevent @dragleave.prevent
                        class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
                        :class="{
                            'border-blue-500 bg-blue-50': isDragOver,
                            'border-red-500 bg-red-50': errors.file
                        }">
                        <div v-if="!selectedFile">
                            <font-awesome-icon icon="cloud-upload-alt" class="text-4xl text-gray-400 mb-4" />
                            <p class="text-lg font-medium text-gray-700 mb-2">
                                Drop your file here, or
                                <button type="button" @click="$refs.fileInput.click()"
                                    class="text-blue-600 hover:text-blue-700 underline">
                                    browse
                                </button>
                            </p>
                            <p class="text-sm text-gray-500">
                                Supported formats: PDF, Word, Excel, PowerPoint, Text, Images (max 200MB)
                            </p>
                        </div>

                        <!-- Selected File -->
                        <div v-else class="text-left">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <div class="p-3 bg-white rounded-lg shadow-sm mr-4">
                                        <font-awesome-icon :icon="getFileIcon(selectedFile.type)"
                                            :class="getFileIconColor(selectedFile.type)" class="text-2xl" />
                                    </div>
                                    <div>
                                        <p class="font-medium text-gray-900">{{ selectedFile.name }}</p>
                                        <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                                    </div>
                                </div>
                                <button type="button" @click="removeFile" class="text-red-500 hover:text-red-700">
                                    <font-awesome-icon icon="times" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <input ref="fileInput" type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                        @change="handleFileSelect" class="hidden" />

                    <p v-if="errors.file" class="mt-2 text-sm text-red-600">{{ errors.file }}</p>
                </div>

                <!-- File Type Info -->
                <div v-if="selectedFile" class="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 class="font-medium text-gray-900 mb-2">File Information</h4>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span class="text-gray-500">Type:</span>
                            <span class="ml-2 font-medium">{{ getFileTypeName(selectedFile.type) }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Size:</span>
                            <span class="ml-2 font-medium">{{ formatFileSize(selectedFile.size) }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Extension:</span>
                            <span class="ml-2 font-medium">{{ getFileExtension(selectedFile.name) }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Last Modified:</span>
                            <span class="ml-2 font-medium">{{ formatDate(selectedFile.lastModified) }}</span>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <base-button @click="$emit('close')" class="bg-gray-300 hover:bg-gray-400 text-gray-700">
                Cancel
            </base-button>
            <base-button @click="uploadGuide" :disabled="!isFormValid || uploading"
                class="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                <div class="h-full flex justify-center items-center" v-if="uploading">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <font-awesome-icon v-else icon="upload" class="mr-2" />
                {{ uploading ? 'Uploading...' : 'Upload Document' }}
            </base-button>
        </div>
    </div>
</template>

<script>
import { gql } from 'graphql-tag'

export default {
    name: 'GuideUploadModal',
    data() {
        return {
            form: {
                name: '',
                description: ''
            },
            selectedFile: null,
            isDragOver: false,
            uploading: false,
            errors: {}
        }
    },
    computed: {
        isFormValid() {
            return this.form.name.trim() && this.selectedFile
        }
    },
    methods: {
        handleDrop(event) {
            this.isDragOver = false
            const files = event.dataTransfer.files
            if (files.length > 0) {
                this.validateAndSetFile(files[0])
            }
        },

        handleFileSelect(event) {
            const file = event.target.files[0]
            if (file) {
                this.validateAndSetFile(file)
            }
        },

        validateAndSetFile(file) {
            this.errors.file = null

            // Check file size (200MB limit)
            const maxSize = 200 * 1024 * 1024 // 200MB in bytes
            if (file.size > maxSize) {
                this.errors.file = 'File size must be less than 200MB'
                return
            }

            // Check file type
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'text/plain',
                'image/jpeg',
                'image/png',
                'image/gif'
            ]

            if (!allowedTypes.includes(file.type)) {
                this.errors.file = 'File type not supported. Please select a valid file.'
                return
            }

            this.selectedFile = file
        },

        removeFile() {
            this.selectedFile = null
            this.errors.file = null
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },

        async uploadGuide() {
            if (!this.isFormValid) return

            this.uploading = true
            this.errors = {}

            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
            mutation UploadGuide($input: UploadGuideInput!) {
              uploadGuide(input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                  description
                  file_type
                  file_size
                  created_at
                }
              }
            }
          `,
                    variables: {
                        input: {
                            name: this.form.name.trim(),
                            description: this.form.description.trim() || null,
                            file: this.selectedFile
                        }
                    }
                })

                if (!response.data.uploadGuide.error) {
                    this.$emit('uploaded', response.data.uploadGuide.data)
                } else {
                    this.$swal({
                        icon: 'error',
                        title: 'Upload Failed',
                        text: response.data.uploadGuide.message || 'Failed to upload document'
                    })
                }
            } catch (error) {
                console.error('Error uploading guide:', error)

                // Handle specific GraphQL errors
                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                    const graphQLError = error.graphQLErrors[0]
                    if (graphQLError.extensions && graphQLError.extensions.validation) {
                        // Handle validation errors
                        const validationErrors = graphQLError.extensions.validation
                        Object.keys(validationErrors).forEach(field => {
                            this.errors[field] = validationErrors[field][0]
                        })
                    } else {
                        this.$swal({
                            icon: 'error',
                            title: 'Upload Failed',
                            text: graphQLError.message || 'Failed to upload document'
                        })
                    }
                } else {
                    this.$swal({
                        icon: 'error',
                        title: 'Upload Failed',
                        text: 'Failed to upload document. Please try again.'
                    })
                }
            } finally {
                this.uploading = false
            }
        },

        getFileIcon(fileType) {
            const iconMap = {
                'application/pdf': 'file-pdf',
                'application/msword': 'file-word',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
                'application/vnd.ms-excel': 'file-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                'application/vnd.ms-powerpoint': 'file-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
                'text/plain': 'file-alt',
                'image/jpeg': 'file-image',
                'image/png': 'file-image',
                'image/gif': 'file-image'
            }
            return iconMap[fileType] || 'file'
        },

        getFileIconColor(fileType) {
            const colorMap = {
                'application/pdf': 'text-red-500',
                'application/msword': 'text-blue-500',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'text-blue-500',
                'application/vnd.ms-excel': 'text-green-500',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'text-green-500',
                'application/vnd.ms-powerpoint': 'text-orange-500',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'text-orange-500',
                'text/plain': 'text-gray-500',
                'image/jpeg': 'text-purple-500',
                'image/png': 'text-purple-500',
                'image/gif': 'text-purple-500'
            }
            return colorMap[fileType] || 'text-gray-500'
        },

        getFileTypeName(fileType) {
            const typeMap = {
                'application/pdf': 'PDF Document',
                'application/msword': 'Word Document (.doc)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document (.docx)',
                'application/vnd.ms-excel': 'Excel Spreadsheet (.xls)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet (.xlsx)',
                'application/vnd.ms-powerpoint': 'PowerPoint Presentation (.ppt)',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation (.pptx)',
                'text/plain': 'Text File',
                'image/jpeg': 'JPEG Image',
                'image/png': 'PNG Image',
                'image/gif': 'GIF Image'
            }
            return typeMap[fileType] || 'Unknown File Type'
        },

        getFileExtension(filename) {
            return filename.split('.').pop().toUpperCase()
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        },

        formatDate(timestamp) {
            return new Date(timestamp).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }
    }
}
</script>
